import { useState, useEffect } from 'react';
import { CalendarOff, Plus, Clock, Calendar, Loader2, CheckCircle2, XCircle, Shield, Briefcase, Undo2, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { workPermissionAPI, compOffAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import BottomNav from '@/components/BottomNav';
import api from '@/lib/api';

export default function EmployeeLeaves() {
  const [activeTab, setActiveTab] = useState('leaves');

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5] pb-28">
      <div className="bg-[#026536] pt-12 pb-3 px-5">
        <div className="max-w-lg mx-auto">
          <h1 data-testid="leaves-title" className="text-xl font-extrabold text-white tracking-tight">Leaves & Permits</h1>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="bg-[#026536] px-5 pb-4">
        <div className="max-w-lg mx-auto flex gap-2 overflow-x-auto scrollbar-hide">
          <button
            data-testid="tab-leaves"
            onClick={() => setActiveTab('leaves')}
            className={`shrink-0 flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'leaves' ? 'bg-white text-[#026536]' : 'bg-white/15 text-white/80 hover:bg-white/20'}`}
          >
            <CalendarOff size={14} className="inline mr-1.5 -mt-0.5" />
            Leaves
          </button>
          <button
            data-testid="tab-comp-off"
            onClick={() => setActiveTab('compoff')}
            className={`shrink-0 flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'compoff' ? 'bg-white text-[#026536]' : 'bg-white/15 text-white/80 hover:bg-white/20'}`}
          >
            <Timer size={14} className="inline mr-1.5 -mt-0.5" />
            Comp-Off
          </button>
          <button
            data-testid="tab-work-permits"
            onClick={() => setActiveTab('permits')}
            className={`shrink-0 flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === 'permits' ? 'bg-white text-[#026536]' : 'bg-white/15 text-white/80 hover:bg-white/20'}`}
          >
            <Shield size={14} className="inline mr-1.5 -mt-0.5" />
            Permits
          </button>
        </div>
      </div>

      <div className="px-4 mt-4 max-w-lg mx-auto">
        {activeTab === 'leaves' ? <LeavesTab /> : activeTab === 'compoff' ? <CompOffTab /> : <WorkPermitsTab />}
      </div>

      <BottomNav />
    </div>
  );
}

function LeavesTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ approved: 0, pending: 0, total: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [leaveType, setLeaveType] = useState('casual');
  const [reason, setReason] = useState('');
  const [durationType, setDurationType] = useState('full_day');
  const [hours, setHours] = useState('');

  useEffect(() => { fetchLeaves(); }, []);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await api.get('/leaves/my-leaves');
      setRecords(res.data.records);
      setStats({ approved: res.data.approved, pending: res.data.pending, total: res.data.total });
    } catch (e) {
      console.error('Leaves fetch failed:', e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLeave = async (leaveId, status) => {
    const action = status === 'approved' ? 'recall' : 'cancel';
    if (!window.confirm(`Are you sure you want to ${action} this leave?`)) return;
    try {
      await api.post(`/leaves/${leaveId}/cancel`);
      toast.success(`Leave ${action}led successfully`);
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} leave`);
    }
  };

  const handleSubmit = async () => {
    if (!startDate) { toast.error('Select a start date'); return; }
    if (durationType === 'full_day' && !endDate) { toast.error('Select an end date'); return; }
    if (durationType === 'hours' && (!hours || parseFloat(hours) <= 0)) { toast.error('Enter valid hours'); return; }
    setSubmitting(true);
    try {
      await api.post('/leaves/request', {
        start_date: startDate,
        end_date: durationType === 'full_day' ? endDate : startDate,
        leave_type: leaveType,
        reason,
        duration_type: durationType,
        hours: durationType === 'hours' ? parseFloat(hours) : null
      });
      toast.success('Leave request submitted!');
      setDialogOpen(false);
      setStartDate(''); setEndDate(''); setReason(''); setHours(''); setDurationType('full_day');
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const statusIcon = {
    approved: <CheckCircle2 size={14} className="text-[#026536]" />,
    rejected: <XCircle size={14} className="text-red-500" />,
    pending: <Clock size={14} className="text-[#dea439]" />,
    cancelled: <Undo2 size={14} className="text-gray-400" />,
  };
  const statusColor = {
    approved: 'bg-[#026536]/10 text-[#026536] border-0',
    rejected: 'bg-red-50 text-red-500 border-0',
    pending: 'bg-[#dea439]/10 text-[#dea439] border-0',
    auto_rejected: 'bg-red-50 text-red-500 border-0',
    cancelled: 'bg-gray-100 text-gray-400 border-0',
  };
  const statusLabel = { approved: 'Approved', rejected: 'Rejected', pending: 'Pending', auto_rejected: 'Quota Exceeded', cancelled: 'Cancelled' };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#1a2529]/50 font-medium">{stats.approved} day{stats.approved !== 1 ? 's' : ''} used, {stats.pending} pending</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="request-leave-btn" size="sm" className="bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-1.5 h-8">
              <Plus size={14} /> Request Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-bold text-[#1a2529]">Request Leave</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Leave Type</Label>
                <Select value={leaveType} onValueChange={setLeaveType}>
                  <SelectTrigger data-testid="leave-type-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="personal">Personal Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Duration</Label>
                <Select value={durationType} onValueChange={(v) => { setDurationType(v); setHours(''); }}>
                  <SelectTrigger data-testid="duration-type-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_day">Full Day(s)</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                    <SelectItem value="hours">Custom Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {durationType === 'hours' && (
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Hours</Label>
                  <Input data-testid="leave-hours" type="number" step="0.5" min="0.5" max="8" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 2, 4, 6" className="h-10 mt-1" />
                  <p className="text-[10px] text-[#1a2529]/30 mt-0.5">Enter hours (0.5 to 8). 4h = half day, 8h = full day.</p>
                </div>
              )}
              <div className={durationType === 'full_day' ? 'grid grid-cols-2 gap-3' : ''}>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">{durationType === 'full_day' ? 'Start Date' : 'Date'}</Label>
                  <Input data-testid="leave-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-10 mt-1" />
                </div>
                {durationType === 'full_day' && (
                  <div>
                    <Label className="text-xs font-semibold text-[#1a2529]/70">End Date</Label>
                    <Input data-testid="leave-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-10 mt-1" />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Reason (optional)</Label>
                <Input data-testid="leave-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for leave" className="h-10 mt-1" />
              </div>
              <Button data-testid="submit-leave-btn" onClick={handleSubmit} disabled={submitting} className="w-full h-11 bg-[#026536] hover:bg-[#026536]/90 font-semibold">
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Submit Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : records.length === 0 ? (
        <div data-testid="no-leaves" className="bg-white rounded-2xl p-8 text-center shadow-sm animate-slide-up">
          <CalendarOff className="mx-auto text-[#1a2529]/20 mb-3" size={40} />
          <p className="font-semibold text-[#1a2529]">No leave requests</p>
          <p className="text-xs text-[#1a2529]/50 mt-1">Tap "Request Leave" to apply</p>
        </div>
      ) : (
        records.map((r, idx) => (
          <div key={r.id} data-testid={`leave-record-${idx}`} className="bg-white rounded-xl p-4 shadow-sm animate-slide-up" style={{ animationDelay: `${idx * 50}ms` }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge className={`${statusColor[r.status]} text-xs font-semibold capitalize`}>
                  {statusIcon[r.status] || statusIcon['rejected']}
                  <span className="ml-1">{statusLabel[r.status] || r.status}</span>
                </Badge>
                <Badge className="bg-[#f4f4f5] text-[#1a2529]/60 border-0 text-xs capitalize">{r.leave_type}</Badge>
                {r.is_lwp && <Badge className="bg-orange-50 text-orange-600 border-0 text-[10px] font-bold">LWP</Badge>}
              </div>
              <span className="text-xs font-semibold text-[#1a2529]">
                {r.duration_type === 'half_day' ? 'Half Day' : r.duration_type === 'hours' ? `${r.hours}h` : `${r.days} day${r.days > 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#1a2529]/60">
              <Calendar size={12} />
              <span>{formatDate(r.start_date)}{r.start_date !== r.end_date && ` - ${formatDate(r.end_date)}`}</span>
            </div>
            {r.reason && <p className="text-xs text-[#1a2529]/40 mt-2 italic">{r.reason}</p>}
            {r.lwp_note && <p className="text-[11px] text-orange-500 mt-1">{r.lwp_note}</p>}
            {r.rejection_reason && <p className="text-[11px] text-red-400 mt-1">{r.rejection_reason}</p>}
            {(r.status === 'pending' || r.status === 'approved') && (
              <button data-testid={`cancel-leave-${idx}`} onClick={() => handleCancelLeave(r.id, r.status)}
                className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:text-red-700 transition-colors">
                <Undo2 size={12} />
                {r.status === 'approved' ? 'Recall Leave' : 'Cancel Request'}
              </button>
            )}
            {r.status === 'cancelled' && (
              <p className="text-[10px] text-[#1a2529]/30 mt-1">Cancelled{r.cancelled_by === 'admin' ? ' by Admin' : ''}{r.cancelled_at ? ` on ${formatDate(r.cancelled_at.split('T')[0])}` : ''}</p>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function WorkPermitsTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [permitType, setPermitType] = useState('holiday_work');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [permitHours, setPermitHours] = useState('');

  useEffect(() => { fetchPermits(); }, []);

  const fetchPermits = async () => {
    setLoading(true);
    try {
      const res = await workPermissionAPI.myRequests();
      setRecords(res.data);
    } catch (e) {
      console.error('Work permits fetch failed:', e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!date) { toast.error('Select a date'); return; }
    if (!reason.trim()) { toast.error('Enter a reason'); return; }
    setSubmitting(true);
    try {
      await workPermissionAPI.request({ permission_type: permitType, date, reason, hours: permitHours ? parseFloat(permitHours) : null });
      toast.success('Work permit request submitted!');
      setDialogOpen(false);
      setDate(''); setReason(''); setPermitHours('');
      fetchPermits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const statusIcon = {
    approved: <CheckCircle2 size={14} className="text-[#026536]" />,
    rejected: <XCircle size={14} className="text-red-500" />,
    pending: <Clock size={14} className="text-[#dea439]" />,
  };
  const statusColor = {
    approved: 'bg-[#026536]/10 text-[#026536] border-0',
    rejected: 'bg-red-50 text-red-500 border-0',
    pending: 'bg-[#dea439]/10 text-[#dea439] border-0',
  };
  const typeLabel = { holiday_work: 'Holiday Work', overtime: 'Overtime', wfh: 'Work from Home' };
  const typeColor = { holiday_work: 'bg-[#dea439]/10 text-[#dea439]', overtime: 'bg-blue-50 text-blue-600', wfh: 'bg-purple-50 text-purple-600' };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#1a2529]/50 font-medium">{records.length} request{records.length !== 1 ? 's' : ''}</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="request-permit-btn" size="sm" className="bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-1.5 h-8">
              <Plus size={14} /> Request Permit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-bold text-[#1a2529]">Request Work Permit</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Permit Type</Label>
                <Select value={permitType} onValueChange={setPermitType}>
                  <SelectTrigger data-testid="permit-type-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="holiday_work">Holiday Work</SelectItem>
                    <SelectItem value="overtime">Overtime</SelectItem>
                    <SelectItem value="wfh">Work from Home</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Date</Label>
                <Input data-testid="permit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Reason</Label>
                <Input data-testid="permit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why do you need this permit?" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Hours</Label>
                <Input data-testid="permit-hours" type="number" step="0.5" min="0.5" max="8" value={permitHours} onChange={(e) => setPermitHours(e.target.value)} placeholder="8 (full day)" className="h-10 mt-1" />
                <p className="text-[10px] text-[#1a2529]/30 mt-0.5">Leave blank for full day (8h)</p>
              </div>
              <Button data-testid="submit-permit-btn" onClick={handleSubmit} disabled={submitting} className="w-full h-11 bg-[#026536] hover:bg-[#026536]/90 font-semibold">
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Submit Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : records.length === 0 ? (
        <div data-testid="no-permits" className="bg-white rounded-2xl p-8 text-center shadow-sm animate-slide-up">
          <Shield className="mx-auto text-[#1a2529]/20 mb-3" size={40} />
          <p className="font-semibold text-[#1a2529]">No work permit requests</p>
          <p className="text-xs text-[#1a2529]/50 mt-1">Request a permit for holiday work or overtime</p>
        </div>
      ) : (
        records.map((r, idx) => (
          <div key={r.id} data-testid={`permit-record-${idx}`} className="bg-white rounded-xl p-4 shadow-sm animate-slide-up" style={{ animationDelay: `${idx * 50}ms` }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge className={`${statusColor[r.status]} text-xs font-semibold capitalize`}>
                  {statusIcon[r.status]}
                  <span className="ml-1">{r.status}</span>
                </Badge>
                <Badge className={`${typeColor[r.permission_type] || 'bg-[#f4f4f5] text-[#1a2529]/60'} border-0 text-xs`}>
                  {typeLabel[r.permission_type] || r.permission_type}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#1a2529]/60">
              <Calendar size={12} />
              <span>{formatDate(r.date)}{r.hours && r.permission_type !== 'wfh' ? ` | ${r.hours}h` : ''}</span>
            </div>
            {r.reason && <p className="text-xs text-[#1a2529]/40 mt-2 italic">{r.reason}</p>}
          </div>
        ))
      )}
    </div>
  );
}


function CompOffTab() {
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hours, setHours] = useState('');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balRes, reqRes] = await Promise.all([compOffAPI.balance(), compOffAPI.myRequests()]);
      setBalance(balRes.data);
      setRequests(reqRes.data);
    } catch (e) { console.error('Comp-off data fetch failed:', e.message); } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    const h = parseFloat(hours);
    if (!h || h <= 0) { toast.error('Enter valid hours'); return; }
    if (!date) { toast.error('Select a date'); return; }
    setSubmitting(true);
    try {
      await compOffAPI.request({ hours: h, date, reason });
      toast.success('Comp-off request submitted!');
      setDialogOpen(false);
      setHours(''); setDate(''); setReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const formatHours = (h) => {
    if (!h || h === 0) return '0h';
    if (h === 8) return '8h';
    if (h === 4) return '4h';
    return `${h}h`;
  };

  const statusColor = {
    approved: 'bg-[#026536]/10 text-[#026536] border-0',
    rejected: 'bg-red-50 text-red-500 border-0',
    pending: 'bg-[#dea439]/10 text-[#dea439] border-0',
  };

  return (
    <div className="space-y-3">
      {/* Balance Card */}
      {balance && (
        <div data-testid="comp-off-balance-card" className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-[#1a2529] flex items-center gap-2">
              <Timer size={15} className="text-[#026536]" /> Comp-Off Balance
            </h3>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-[#026536]/5 rounded-xl p-3 text-center">
              <p className="text-lg font-extrabold text-[#026536]">{formatHours(balance.earned)}</p>
              <p className="text-[9px] text-[#026536]/60 font-semibold uppercase mt-0.5">Earned</p>
            </div>
            <div className="bg-[#026536]/10 rounded-xl p-3 text-center">
              <p className="text-lg font-extrabold text-[#026536]">{formatHours(balance.used)}</p>
              <p className="text-[9px] text-[#026536]/60 font-semibold uppercase mt-0.5">Used</p>
            </div>
            <div className="bg-[#dea439]/10 rounded-xl p-3 text-center">
              <p className="text-lg font-extrabold text-[#dea439]">{formatHours(balance.pending)}</p>
              <p className="text-[9px] text-[#dea439]/60 font-semibold uppercase mt-0.5">Pending</p>
            </div>
            <div className={`rounded-xl p-3 text-center ${balance.remaining > 0 ? 'bg-[#026536]/5' : 'bg-[#f4f4f5]'}`}>
              <p className={`text-lg font-extrabold ${balance.remaining > 0 ? 'text-[#026536]' : 'text-[#1a2529]/30'}`}>{formatHours(balance.remaining)}</p>
              <p className="text-[9px] text-[#1a2529]/40 font-semibold uppercase mt-0.5">Available</p>
            </div>
          </div>
          <p className="text-[10px] text-[#1a2529]/30 mt-2 text-center">
            Earned from RM-approved overtime & holiday work permits
          </p>
        </div>
      )}

      {/* Request Button */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#1a2529]/50 font-medium">{requests.length} request{requests.length !== 1 ? 's' : ''}</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="request-comp-off-btn" size="sm" className="bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-1.5 h-8" disabled={!balance || balance.remaining <= 0}>
              <Plus size={14} /> Use Comp-Off
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-bold text-[#1a2529]">Use Comp-Off</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Hours</Label>
                <Input data-testid="comp-off-hours" type="number" step="0.5" min="0.5" max={balance?.remaining || 24} value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 4 (half day), 8 (full day)" className="h-10 mt-1" />
                <p className="text-[10px] text-[#1a2529]/30 mt-0.5">Available: {balance?.remaining || 0}h. 4h = half day, 8h = full day.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Date</Label>
                <Input data-testid="comp-off-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Reason (optional)</Label>
                <Input data-testid="comp-off-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for taking comp-off" className="h-10 mt-1" />
              </div>
              <Button data-testid="submit-comp-off-btn" onClick={handleSubmit} disabled={submitting} className="w-full h-11 bg-[#026536] hover:bg-[#026536]/90 font-semibold">
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Submit Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div data-testid="no-comp-off" className="bg-white rounded-2xl p-8 text-center shadow-sm">
          <Timer className="mx-auto text-[#1a2529]/20 mb-3" size={40} />
          <p className="font-semibold text-[#1a2529]">No comp-off requests</p>
          <p className="text-xs text-[#1a2529]/50 mt-1">Earn hours via approved overtime/holiday work permits, then use them here.</p>
        </div>
      ) : (
        requests.map((r, idx) => (
          <div key={r.id} data-testid={`comp-off-record-${idx}`} className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Badge className={`${statusColor[r.status]} text-xs font-semibold capitalize`}>
                {r.status === 'approved' ? <CheckCircle2 size={12} className="mr-1" /> : r.status === 'rejected' ? <XCircle size={12} className="mr-1" /> : <Clock size={12} className="mr-1" />}
                {r.status}
              </Badge>
              <span className="text-xs font-bold text-[#1a2529]">{formatHours(r.hours)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#1a2529]/60">
              <Calendar size={12} />
              <span>{formatDate(r.date)}</span>
            </div>
            {r.reason && <p className="text-xs text-[#1a2529]/40 mt-2 italic">{r.reason}</p>}
          </div>
        ))
      )}
    </div>
  );
}
